import React from 'react';

import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { Style } from '@react-pdf/types';
import { RowType } from 'PDF/components/FinBlockTable/FinBlockTable';
import { getColorObjectStyleByRecord } from 'PDF/helpers/documentStyle';

import { getTranslatedInitialValue } from 'helpers/getTranslatedInitialValue';
import { formatNumber } from 'helpers/numbers';

interface IInfo {
  type: RowType;
  heading: string;
  values?: (number | string)[];
  valuesType?: RowType;
}

interface IRowProps {
  data: IInfo;
  isRatiosTable?: boolean;
  isAlwaysNumberFloat?: boolean;
  firstColumnWidth?: number;
  noBorderBeforeTotal?: boolean;
}

const colorIndexesBoldGreenText = {
  green: 7,
  blue: 0,
  red: 1,
  blueGreen: 6,
  blueRed: 0,
  blueYellow: 0,
};

const colorIndexesBoldBackgroundGray = {
  green: 32,
  blue: 2,
  red: 2,
  blueGreen: 32,
  blueRed: 2,
  blueYellow: 32,
};

const colorIndexesBoldDarkGreen = {
  green: 12,
  blue: 5,
  red: 5,
  blueGreen: 5,
  blueRed: 1,
  blueYellow: 29,
};

export const Row: React.FC<IRowProps> = ({
  data,
  isRatiosTable = false,
  isAlwaysNumberFloat = false,
  firstColumnWidth,
  noBorderBeforeTotal,
}) => {
  const width = isRatiosTable ? '130px' : '160px';
  const headerColumnWidth = firstColumnWidth ?? width;

  const getTypeStyle = (type: string) => {
    switch (type) {
      case 'boldBlackText':
        return styles.boldBlackText;
      case 'regularText':
        return styles.regularText;
      case 'boldGreenText':
        return getColorObjectStyleByRecord(colorIndexesBoldGreenText, 'color');
      case 'regularGrayText':
        return styles.regularGrayText;
      case 'boldBackgroundGray':
        return {
          ...styles.boldBackgroundGray,
          ...getColorObjectStyleByRecord(colorIndexesBoldBackgroundGray, 'backgroundColor'),
        };
      case 'boldDarkGreen':
        return getColorObjectStyleByRecord(colorIndexesBoldDarkGreen, 'color');
      default:
        return {};
    }
  };

  const getNumberView = (value: string | number) => {
    if (typeof value === 'string' && value.startsWith('-')) {
      return `(${value.slice(1)})`;
    } else if (isNaN(+value) || value === '') {
      return value;
    } else if (Number(value) < 0) {
      return `(${formatNumber(+value * -1, 1, isAlwaysNumberFloat)})`;
    } else {
      return formatNumber(+value, 1, isAlwaysNumberFloat);
    }
  };

  return (
    <View
      style={[
        styles.row,
        getTypeStyle(data.type) as Style,
        { borderBottom: noBorderBeforeTotal ? 'none' : '0.5px solid #CECECE' },
      ]}
    >
      <View
        style={[styles.heading, getTypeStyle(data.type) as Style, { minWidth: headerColumnWidth, maxWidth: headerColumnWidth }]}
      >
        <Text>{data.heading}</Text>
      </View>
      {data.values?.map((value, index) => (
        <View
          key={index}
          style={[
            styles.ceil,
            getTypeStyle(data.valuesType ?? data.type) as Style,
            isRatiosTable && index === 0 ? { minWidth: '81px', maxWidth: '81px' } : { width: '100%' },
            ...(firstColumnWidth && index === 0 ? [{ minWidth: 'auto', maxWidth: 'auto' }] : []),
          ]}
        >
          <Text>{getNumberView(value)}</Text>
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  ceil: {
    fontFamily: 'Noto',
    fontWeight: 600,
    fontSize: '9px',
    color: '#fff',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    width: '100%',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    minHeight: '18px',
    paddingRight: '5px',
  },
  heading: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontFamily: 'Noto',
    fontWeight: 600,
    fontSize: '9px',
    paddingLeft: '5px',
  },
  boldBlackText: {
    color: '#000000',
  },
  regularText: {
    fontWeight: 300,
    color: '#000000',
  },
  boldGreenText: {
    color: '#6DB068',
  },
  regularGrayText: {
    fontWeight: 400,
    color: '#C6C6C9',
  },
  boldBackgroundGray: {
    fontWeight: 600,
    color: '#000',
    border: 'none',
  },
});
